<!-- last modified by AI tools on 2026-09-30 -->
<script setup lang="ts">
/**
 * ElectionDetailsDesktop Component
 *
 * Desktop-only variant of the election details panel using a PrimeVue Dialog.
 * Delegates all state management to the shared useElectionDetailsState composable.
 *
 * Used for viewports >= 1024px (lg breakpoint).
 */
import { inject, type MaybeRefOrGetter } from 'vue';
import { useRoute } from 'vue-router';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import ProgressSpinner from 'primevue/progressspinner';
import CandidateCard from './CandidateCard.vue';
import CandidateAvatar from '@/components/CandidateAvatar.vue';
import ElectionChart from './ElectionChart.vue';
import ChartPeriodSelector from './ChartPeriodSelector.vue';
import ElectionActivity from './ElectionActivity.vue';
import ElectionNews from './ElectionNews.vue';
import LoginCTA from './LoginCTA.vue';
import type { ForecastTraderElectionMapProduct } from '@/types/forecasttrader.types';
import { useElectionDetailsState, type UseElectionDetailsStateOptions } from '../composables/useElectionDetailsState';
import { useUserState } from '@/stores/useUserState';
import { useI18n } from 'vue-i18n';
import { isPartyStrikeName } from '../utils/partyUtils';
import { formatCompactNumber } from '@/utils/formatNumber';
import ExchangeAvatarGroup from '@/components/ExchangeAvatarGroup.vue';
import BallotpediaLink from './BallotpediaLink.vue';
import { useNavigation } from '@/composables/useNavigation';
import {
  buildPredictionMarketsUrl,
  ELECTION_BOARD_TAB_KEY,
  type ElectionBoardView,
} from '../utils/predictionMarketsNavigation';
import PredictionMarketsCta from './PredictionMarketsCta.vue';

const userState = useUserState();
const { t } = useI18n();

// Props - matches UseElectionDetailsStateOptions
const props = defineProps<{
  /** Whether the dialog is visible */
  visible: boolean;
  /** Available election products for this state */
  products: ForecastTraderElectionMapProduct[];
  /** Initial product conid for pre-selecting dropdown (from table click or deep link) */
  initialProductConid?: number;
  /** Initial candidate conid for opening single-candidate view (from table click or deep link) */
  initialCandidateConid?: number;
  /** Leading party from API ('Democrat', 'Republican', etc.) */
  party?: string;
  /** Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid) */
  color?: number;
}>();

// Emits
const emit = defineEmits<{
  /** Update dialog visibility */
  'update:visible': [value: boolean];
  /** Share this election. When `conid` is provided, share targets a single candidate; otherwise share is product-level. */
  share: [conid?: number];
  /** Toggle watchlist */
  toggleWatchlist: [];
  /** Navigate to election grid view */
  showGrid: [options?: { filters?: string }];
}>();

// Dialog-specific props
const dialogProps = {
  modal: true,
  closable: false,
  dismissableMask: true,
  draggable: false,
  style: { width: '100%', maxWidth: '900px', height: '80vh', minHeight: '600px' },
  pt: {
    mask: 'eh-panel-zindex',
    root: 'ratio-design-provider !rounded-xl overflow-hidden bg-surface-high',
    content: '!p-0 !h-full flex flex-col',
  },
};

// Build composable options from props (reactive getters)
const stateOptions: UseElectionDetailsStateOptions = {
  products: (() => props.products) as MaybeRefOrGetter<ForecastTraderElectionMapProduct[]>,
  initialProductConid: (() => props.initialProductConid) as MaybeRefOrGetter<number | undefined>,
  initialCandidateConid: (() => props.initialCandidateConid) as MaybeRefOrGetter<number | undefined>,
  visible: (() => props.visible) as MaybeRefOrGetter<boolean>,
  party: (() => props.party) as MaybeRefOrGetter<string | undefined>,
  color: (() => props.color) as MaybeRefOrGetter<number | undefined>,
};

// All reactive state from the shared composable
const state = useElectionDetailsState(stateOptions);
const route = useRoute();
const { openUrl } = useNavigation();
const electionBoardTab = inject(ELECTION_BOARD_TAB_KEY, undefined);

function openPredictionMarkets() {
  const productConid = state.activeProductConid.value;
  const exchange = state.marketInfo.value?.exchange;
  if (!productConid || !exchange) return;
  const origin = state.viewMode.value === 'single-candidate' ? 'single' : 'multi';
  const view: ElectionBoardView = route.meta.viewMode === 'dashboard' ? 'map' : 'grid';
  openUrl(
    buildPredictionMarketsUrl({
      productConid,
      exchange,
      origin,
      view,
      tab: electionBoardTab?.value,
      candidateConid: origin === 'single' ? (state.selectedCandidateConid.value ?? undefined) : undefined,
    }),
  );
}

// Local handlers that delegate to emits
function closeDialog() {
  emit('update:visible', false);
}

function handleShare(conid?: number) {
  emit('share', conid);
}
</script>

<template>
  <Dialog :visible="visible" v-bind="dialogProps" @update:visible="emit('update:visible', $event)">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center gap-3 overflow-hidden">
          <div
            v-if="state.electionContract.value"
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-black text-white shadow-sm sm:h-12 sm:w-12 sm:rounded-2xl sm:text-lg"
            :style="{ backgroundColor: state.badgeColor.value }"
          >
            {{ state.electionContract.value.stateCode }}
          </div>
          <div
            v-else
            class="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-surface-200 sm:h-12 sm:w-12 sm:rounded-2xl"
          />

          <div class="min-w-0 flex-1">
            <div v-if="state.isLoadingContracts.value" class="space-y-2">
              <div class="h-6 w-3/4 animate-pulse rounded bg-surface-200" />
              <div class="h-4 w-1/2 animate-pulse rounded bg-surface-200" />
            </div>
            <div v-else-if="state.electionContract.value" class="space-y-1">
              <div class="flex items-center gap-2">
                <p class="truncate text-lg font-black leading-tight sm:text-2xl">
                  {{ state.marketInfo.value?.name || state.electionContract.value.name }}
                </p>
                <!-- Product Dropdown Integration in Header (hidden in single candidate view) -->
                <Dropdown
                  v-if="state.availableProducts.value.length > 1 && state.viewMode.value === 'multi-candidate'"
                  v-model="state.selectedProduct.value"
                  :options="state.availableProducts.value"
                  optionLabel="name"
                  :placeholder="t('selectProduct')"
                  class="!w-48 !text-xs text-on-surface"
                  :pt="{
                    root: '!border-surface-300 !rounded-lg !text-xs !h-8 !bg-surface',
                    input: '!text-xs !py-1 !px-2',
                    trigger: '!w-6',
                    panel: '!text-xs',
                    item: '!text-xs !py-1',
                  }"
                />
              </div>
              <div
                class="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-bold text-muted-color sm:text-xs"
              >
                <span class="flex items-center gap-1 whitespace-nowrap">
                  <span class="mt-[2px]"
                    >{{ formatCompactNumber(state.electionContract.value.openInterest) }} {{ t('openInterest') }}</span
                  >
                </span>
              </div>
            </div>
            <div v-else-if="state.contractError.value" class="text-sm text-error">
              {{ t('errorLoadingElectionData') }}
            </div>
          </div>
        </div>

        <div class="flex shrink-0 items-center gap-2">
          <Button
            icon="ibi-share-send"
            severity="secondary"
            variant="text"
            rounded
            :title="t('share')"
            :pt="{
              icon: '!text-2xl !text-on-surface-secondary',
            }"
            @click="handleShare()"
          />
          <Button
            icon="ibi-close"
            :pt="{ icon: '!text-2xl !text-on-surface-secondary' }"
            severity="secondary"
            variant="text"
            rounded
            @click="closeDialog"
          />
        </div>
      </div>
    </template>

    <!-- Loading State -->
    <div v-if="state.isLoadingContracts.value" class="flex flex-1 w-full items-center justify-center p-8">
      <div class="text-center">
        <ProgressSpinner class="!h-8 !w-8" />
        <p class="mt-4 text-sm text-surface-500">{{ t('loadingElectionData') }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="state.contractError.value" class="flex flex-1 w-full items-center justify-center p-8">
      <div class="text-center">
        <i class="ibi-warning h-12 w-12 text-surface-400" />
        <p class="mt-4 text-lg font-medium text-surface-900">{{ t('unableToLoadElectionData') }}</p>
        <p class="mt-2 text-sm text-surface-500">{{ state.contractError.value.message }}</p>
        <button
          class="mt-4 rounded-lg bg-surface-100 px-4 py-2 text-sm font-medium text-surface-900 hover:bg-surface-200"
          @click="state.selectedProduct.value = state.availableProducts.value[0]"
        >
          {{ t('tryAgain') }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div
      v-else-if="state.electionContract.value && state.actualCandidates.value"
      class="flex-1 overflow-y-auto !px-0 md:!px-4 pt-2 pb-4 sm:space-y-10 sm:px-8 sm:pt-4 sm:pb-8"
    >
      <LoginCTA />

      <!-- Multi-Candidate View -->
      <div v-if="state.viewMode.value === 'multi-candidate'" class="space-y-8">
        <!-- Candidate Comparison Cards Grid -->
        <div
          :class="[
            'grid gap-6',
            state.hasSingleCandidate.value ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 sm:grid-cols-2',
          ]"
        >
          <div
            v-for="candidate in state.filteredCandidates.value?.candidates || []"
            :key="candidate.conid"
            :class="[
              'flex flex-col rounded-2xl border p-4 sm:p-6',
              candidate.party === 'DEM' && 'border-democrat-subtle bg-democrat-subtle/10',
              candidate.party === 'GOP' && 'border-republican-subtle bg-republican-subtle/10',
              candidate.party === 'IND' && 'border-independent-subtle bg-independent-subtle/10',
              candidate.party === 'GRN' && 'border-green-subtle bg-green-subtle/10',
            ]"
          >
            <!-- Candidate Profile Section -->
            <div class="flex flex-col items-center text-center mb-4 sm:mb-6">
              <!-- Header row: exchange icons (left) | avatar (center) | spacer (right) -->
              <div class="flex w-full items-start justify-center gap-4 mb-3 sm:mb-4">
                <div class="flex flex-1 items-center min-w-0">
                  <ExchangeAvatarGroup :exchanges="candidate.exchanges" :exchange="candidate.exchange" size="small" />
                </div>
                <div class="relative shrink-0">
                  <div
                    :class="[
                      'h-20 w-20 overflow-hidden rounded-2xl ring-4 ring-offset-3 sm:h-28 sm:w-28 sm:rounded-3xl',
                      !state.hasSingleCandidate.value && 'cursor-pointer transition-transform hover:scale-105',
                      candidate.party === 'DEM' && 'ring-democrat-subtle',
                      candidate.party === 'GOP' && 'ring-republican-subtle',
                      candidate.party === 'IND' && 'ring-independent-subtle',
                      candidate.party === 'GRN' && 'ring-green-subtle',
                    ]"
                    @click="state.handleCandidatePhotoClick(candidate.conid)"
                  >
                    <CandidateAvatar
                      :conid="candidate.conid"
                      :candidate-name="candidate.name"
                      :party-code="candidate.party"
                      size="fill"
                    />
                  </div>
                  <div
                    :class="[
                      'absolute -bottom-1 -right-1 rounded-md px-2 py-0.5 text-[8px] font-black uppercase tracking-wider text-white sm:-bottom-2 sm:-right-2 sm:rounded-lg sm:px-3 sm:py-1 sm:text-[10px]',
                      candidate.party === 'DEM' && 'bg-democrat',
                      candidate.party === 'GOP' && 'bg-republican',
                      candidate.party === 'IND' && 'bg-independent',
                      candidate.party === 'GRN' && 'bg-green',
                      isPartyStrikeName(candidate.name) && 'invisible',
                    ]"
                  >
                    {{ candidate.party }}
                  </div>
                  <div
                    v-if="candidate.isIncumbent"
                    class="absolute -left-1 -top-1 rounded-md bg-surface-inverse px-1.5 py-0.5 text-[7px] font-black uppercase tracking-tight text-on-surface-inverse shadow-lg sm:-left-2 sm:-top-2 sm:px-2 sm:py-1 sm:text-[8px]"
                  >
                    {{ t('incumbent') }}
                  </div>
                </div>
                <div class="flex flex-1 items-center justify-end gap-3 min-w-0">
                  <button
                    class="shrink-0 cursor-pointer text-muted-color hover:text-on-surface transition-colors"
                    :title="t('share')"
                    @click.stop="handleShare(candidate.conid)"
                  >
                    <i class="ibi-share-send text-base" />
                  </button>
                  <BallotpediaLink
                    :name="candidate.name"
                    :aria-label="`Learn more about ${candidate.name} on Ballotpedia`"
                    class="shrink-0 text-muted-color hover:text-on-surface transition-colors"
                    @click.stop
                  >
                    <i class="ibi-arrow-up-right text-base" />
                  </BallotpediaLink>
                </div>
              </div>
              <p class="line-clamp-1 font-black text-surface-900 text-lg sm:text-xl">{{ candidate.name }}</p>
              <p
                :class="[
                  'line-clamp-1 text-[10px] font-bold uppercase tracking-widest sm:text-xs',
                  candidate.party === 'DEM' && 'text-democrat',
                  candidate.party === 'GOP' && 'text-republican',
                  candidate.party === 'IND' && 'text-independent',
                  candidate.party === 'GRN' && 'text-green',
                  isPartyStrikeName(candidate.name) && 'invisible',
                ]"
              >
                {{ candidate.partyAffiliation }}
              </p>
            </div>

            <!-- Candidate Trading Section -->
            <CandidateCard :candidate="candidate" class="!border-0 !bg-transparent !p-0" />
          </div>
        </div>

        <PredictionMarketsCta v-if="state.hasMoreContracts.value" @click="openPredictionMarkets" />

        <!-- Price History Chart -->
        <div class="rounded-3xl bg-surface-container p-4 sm:p-6 md:p-8 overflow-hidden">
          <div class="mb-6 flex flex-col justify-between gap-4 sm:mb-8 sm:flex-row sm:items-center">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <p class="whitespace-nowrap font-black sm:text-base">{{ t('priceMarketMomentum') }}</p>
              <SelectButton
                v-model="state.chartMode.value"
                :options="[...state.chartModeOptions]"
                option-label="label"
                option-value="value"
                size="small"
                :pt="{
                  root: 'border-0 bg-surface-200/50 rounded-xl p-0.5 bg-surface-high',
                  button: 'px-3 py-1 text-[9px] font-black uppercase tracking-wider rounded-lg',
                }"
              />
            </div>
            <div class="no-scrollbar flex items-center gap-3 overflow-x-auto py-1 sm:gap-4">
              <div
                v-if="state.filteredCandidates.value?.candidates?.some((c) => c.party === 'DEM')"
                class="flex items-center gap-1.5 whitespace-nowrap"
              >
                <div class="h-2.5 w-2.5 rounded-full bg-democrat" />
                <span class="text-[9px] font-bold uppercase text-surface-500">{{ t('dem') }}</span>
              </div>
              <div
                v-if="state.filteredCandidates.value?.candidates?.some((c) => c.party === 'GOP')"
                class="flex items-center gap-1.5 whitespace-nowrap"
              >
                <div class="h-2.5 w-2.5 rounded-full bg-republican" />
                <span class="text-[9px] font-bold uppercase text-surface-500">{{ t('gop') }}</span>
              </div>
              <div
                v-if="state.filteredCandidates.value?.candidates?.some((c) => c.party === 'IND')"
                class="flex items-center gap-1.5 whitespace-nowrap"
              >
                <div class="h-2.5 w-2.5 rounded-full bg-independent" />
                <span class="text-[9px] font-bold uppercase text-surface-500">{{ t('ind') }}</span>
              </div>
            </div>
          </div>
          <ChartPeriodSelector
            v-model="state.chartPeriod.value"
            :options="[...state.chartPeriodOptions]"
            class="mb-3"
          />

          <div class="w-full h-[280px] sm:h-[320px] overflow-hidden">
            <ElectionChart
              :conids="state.chartConids.value"
              :series-colors="state.chartColors.value"
              :chart-type="state.chartMode.value === 'line' ? 'Line' : 'Area'"
              :chart-height="320"
              :period="state.chartPeriodConfig.value.period"
              :step="state.chartPeriodConfig.value.step"
            />
          </div>
        </div>
      </div>

      <!-- Single Candidate View -->
      <div v-else-if="state.viewMode.value === 'single-candidate'" class="space-y-8 sm:space-y-10">
        <!-- Loading State for Single Candidate -->
        <div v-if="state.isLoadingDetails.value" class="flex flex-1 w-full items-center justify-center">
          <div class="text-center">
            <ProgressSpinner class="!h-8 !w-8" />
            <p class="mt-4 text-sm text-surface-500">{{ t('loadingCandidateDetails') }}</p>
          </div>
        </div>

        <!-- Error State for Single Candidate -->
        <div v-else-if="state.detailsError.value" class="flex flex-1 w-full items-center justify-center">
          <div class="text-center">
            <i class="ibi-warning h-12 w-12 text-surface-400" />
            <p class="mt-4 text-lg font-medium text-surface-900">{{ t('unableToLoadCandidateDetails') }}</p>
            <p class="mt-2 text-sm text-surface-500">{{ state.detailsError.value.message }}</p>
            <button
              class="mt-4 rounded-lg bg-surface-100 px-4 py-2 text-sm font-medium text-surface-900 hover:bg-surface-200"
              @click="state.handleBackToMultiView()"
            >
              {{ t('backToCandidates') }}
            </button>
          </div>
        </div>

        <!-- Single Candidate Content -->
        <div v-else-if="state.candidateDetails.value" class="space-y-8 sm:space-y-10">
          <!-- Back Button for Single Candidate View -->
          <div class="flex justify-start">
            <button
              class="flex items-center shrink-0 cursor-pointer rounded-full px-4 py-2 bg-surface-container uppercase transition-all"
              @click="state.handleBackToMultiView()"
            >
              <i class="ibi-arrow-left h-4 w-4" />
              <span class="text-xs font-bold uppercase tracking-wider"> {{ t('viewGeneralElection') }}</span>
            </button>
          </div>

          <!-- Find the selected candidate from actualCandidates (use full list for single view) -->
          <template v-for="candidate in state.actualCandidates.value.candidates" :key="candidate.conid">
            <div v-if="candidate.conid === state.selectedCandidateConid.value" class="space-y-8 sm:space-y-10">
              <!-- Unified Candidate Card Container for Single View -->
              <div class="mx-auto max-w-md">
                <div
                  :class="[
                    'flex h-full flex-col rounded-2xl border p-4 sm:p-6',
                    candidate.party === 'DEM' && 'border-democrat-subtle bg-democrat-subtle/20',
                    candidate.party === 'GOP' && 'border-republican-subtle bg-republican-subtle/20',
                    candidate.party === 'IND' && 'border-independent-subtle bg-independent-subtle/20',
                    candidate.party === 'GRN' && 'border-green-subtle bg-green-subtle/20',
                  ]"
                >
                  <!-- Candidate Header Section -->
                  <div class="text-center mb-6 sm:mb-8">
                    <!-- Header row: exchange icons (left) | avatar (center) | spacer (right) -->
                    <div class="flex w-full items-start justify-center gap-4 mb-6 sm:mb-8">
                      <div class="flex flex-1 items-center min-w-0">
                        <ExchangeAvatarGroup
                          :exchanges="candidate.exchanges"
                          :exchange="candidate.exchange"
                          size="small"
                        />
                      </div>
                      <div class="relative shrink-0">
                        <div
                          :class="[
                            'h-32 w-32 overflow-hidden rounded-3xl ring-4 ring-offset-4 sm:h-40 sm:w-40 sm:rounded-4xl',
                            candidate.party === 'DEM' && 'ring-democrat-subtle',
                            candidate.party === 'GOP' && 'ring-republican-subtle',
                            candidate.party === 'IND' && 'ring-independent-subtle',
                            candidate.party === 'GRN' && 'ring-green-subtle',
                          ]"
                        >
                          <CandidateAvatar
                            :conid="candidate.conid"
                            :candidate-name="candidate.name"
                            :party-code="candidate.party"
                            size="fill"
                          />
                        </div>
                        <div
                          :class="[
                            'absolute -bottom-2 -right-2 rounded-lg px-3 py-1 text-xs font-black uppercase tracking-wider text-white sm:-bottom-3 sm:-right-3 sm:rounded-xl sm:px-4 sm:py-2 sm:text-sm',
                            candidate.party === 'DEM' && 'bg-democrat',
                            candidate.party === 'GOP' && 'bg-republican',
                            candidate.party === 'IND' && 'bg-independent',
                            candidate.party === 'GRN' && 'bg-green',
                            isPartyStrikeName(candidate.name) && 'invisible',
                          ]"
                        >
                          {{ candidate.party }}
                        </div>
                        <div
                          v-if="candidate.isIncumbent"
                          class="absolute -left-2 -top-2 rounded-lg bg-surface-inverse px-2 py-1 text-xs font-black uppercase tracking-tight text-on-surface-inverse shadow-xl sm:-left-3 sm:-top-3 sm:px-3 sm:py-2 z-10"
                        >
                          Incumbent
                        </div>
                      </div>
                      <div class="flex flex-1 items-center justify-end gap-3 min-w-0">
                        <button
                          class="shrink-0 cursor-pointer text-muted-color hover:text-on-surface transition-colors"
                          :title="t('share')"
                          @click.stop="handleShare(candidate.conid)"
                        >
                          <i class="ibi-share-send text-base" />
                        </button>
                        <BallotpediaLink
                          :name="candidate.name"
                          :aria-label="`Learn more about ${candidate.name} on Ballotpedia`"
                          class="shrink-0 text-muted-color hover:text-on-surface transition-colors"
                          @click.stop
                        >
                          <i class="ibi-arrow-up-right text-base" />
                        </BallotpediaLink>
                      </div>
                    </div>
                    <h2 class="text-3xl font-black text-surface-900 sm:text-4xl">{{ candidate.name }}</h2>
                    <p
                      :class="[
                        'mt-2 text-sm font-bold uppercase tracking-widest sm:text-base',
                        candidate.party === 'DEM' && 'text-democrat',
                        candidate.party === 'GOP' && 'text-republican',
                        candidate.party === 'IND' && 'text-independent',
                        candidate.party === 'GRN' && 'text-green',
                        isPartyStrikeName(candidate.name) && 'invisible',
                      ]"
                    >
                      {{ candidate.partyAffiliation }}
                    </p>
                  </div>

                  <!-- Candidate Trading Section -->
                  <CandidateCard :candidate="candidate" class="!border-0 !bg-transparent !p-0" />
                </div>
              </div>

              <PredictionMarketsCta v-if="state.hasMoreContracts.value" @click="openPredictionMarkets" />

              <!-- Single Candidate Price Chart -->
              <div class="rounded-3xl bg-surface-container p-4 sm:p-6 md:p-8 overflow-hidden">
                <div class="mb-6 flex flex-col justify-between gap-4 sm:mb-8 sm:flex-row sm:items-center">
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                    <p class="whitespace-nowrap font-black sm:text-base">{{ t('priceMarketMomentum') }}</p>
                    <SelectButton
                      v-model="state.chartMode.value"
                      :options="[...state.chartModeOptions]"
                      option-label="label"
                      option-value="value"
                      size="small"
                      :pt="{
                        root: 'border-0 bg-surface-200/50 rounded-xl p-0.5 bg-surface-high',
                        button: 'px-3 py-1 text-[9px] font-black uppercase tracking-wider rounded-lg',
                      }"
                    />
                  </div>
                  <div class="no-scrollbar flex items-center gap-3 overflow-x-auto py-1 sm:gap-4">
                    <div class="flex items-center gap-1.5 whitespace-nowrap">
                      <div
                        :class="[
                          'h-3.5 w-3.5 rounded-full',
                          candidate.party === 'DEM' && 'bg-democrat',
                          candidate.party === 'GOP' && 'bg-republican',
                          candidate.party === 'IND' && 'bg-independent',
                          candidate.party === 'GRN' && 'bg-green',
                        ]"
                      />
                      <span class="text-sm font-bold uppercase text-surface-500">{{
                        candidate.name.split(' ').pop()
                      }}</span>
                    </div>
                  </div>
                </div>

                <ChartPeriodSelector
                  v-model="state.chartPeriod.value"
                  :options="[...state.chartPeriodOptions]"
                  class="mb-3"
                />

                <div class="w-full h-[280px] sm:h-[320px] overflow-hidden">
                  <ElectionChart
                    :conids="state.chartConids.value"
                    :series-colors="state.chartColors.value"
                    :chart-type="state.chartMode.value === 'line' ? 'Line' : 'Area'"
                    :chart-height="320"
                    :period="state.chartPeriodConfig.value.period"
                    :step="state.chartPeriodConfig.value.step"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- My Activity (auth-only) -->
      <ElectionActivity v-if="userState.authenticated" class="mt-8" :conids="state.productConids.value" />

      <!-- Related News (auth-only) -->
      <ElectionNews
        v-if="userState.authenticated && state.activeProductConid.value"
        class="mt-8"
        :conid="state.activeProductConid.value"
      />
    </div>
  </Dialog>
</template>
